import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, ShieldOff, ShieldCheck, LogIn, Users2, Receipt, UserPlus, Trash2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function SuperAdmin() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [biz, setBiz] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ business_name: "", service_type: "", owner_full_name: "", owner_email: "", owner_password: "" });
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce the query so we don't hammer the server on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // The list is now server-filtered (older tenants live past the default
  // page cap, so client-side filtering alone can't find them).
  const filteredBiz = biz;

  const load = async () => {
    setLoading(true);
    try {
      const params = debouncedQuery ? { q: debouncedQuery } : {};
      const { data } = await api.get("/superadmin/businesses", { params });
      setBiz(data);
    } catch { toast.error("Failed to load businesses"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [debouncedQuery]);

  if (!user?.is_super_admin) {
    return <div className="p-8 text-zinc-500">Super-admin only.</div>;
  }

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/superadmin/businesses", form);
      toast.success("Business created");
      setOpen(false);
      setForm({ business_name: "", service_type: "", owner_full_name: "", owner_email: "", owner_password: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (b) => {
    try {
      const path = b.disabled ? "enable" : "disable";
      await api.post(`/superadmin/businesses/${b.id}/${path}`);
      toast.success(`Business ${b.disabled ? "enabled" : "disabled"}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const impersonate = async (b) => {
    if (b.id === user.business_id) { toast.info("This is your own business"); return; }
    try {
      const { data } = await api.post(`/superadmin/impersonate/${b.id}`);
      if (data.token) localStorage.setItem("lf_token", data.token);
      await refresh();
      toast.success(`Viewing as ${b.business_name}`);
      nav("/app");
    } catch { toast.error("Failed to impersonate"); }
  };

  const del = async (b) => {
    if (b.id === user.business_id) { toast.error("Cannot delete your own business"); return; }
    const name = window.prompt(`Type the business name exactly to permanently delete "${b.business_name}" and ALL its data (leads, jobs, invoices, files):`);
    if (name === null) return;
    if (name !== b.business_name) { toast.error("Name did not match — not deleted"); return; }
    try {
      await api.delete(`/superadmin/businesses/${b.id}`, { params: { confirm_name: name } });
      toast.success(`Deleted ${b.business_name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Platform</div>
          <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-[#FF5A00]"/> Super Admin
          </h1>
          <p className="text-zinc-500 mt-1">Manage all businesses on LeadForge. Data is fully isolated between tenants.</p>
        </div>
        <Button onClick={()=>setOpen(true)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="superadmin-create-btn">
          <Plus className="w-4 h-4 mr-1"/> New business
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
        <div className="px-6 py-3 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search businesses by name, service, owner name, or email…"
              className="pl-9 pr-9"
              data-testid="superadmin-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-700"
                aria-label="Clear search"
                data-testid="superadmin-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="text-xs text-zinc-500 font-mono-accent uppercase tracking-widest" data-testid="superadmin-search-count">
            {loading ? "loading…" : `${filteredBiz.length} of ${biz.length}`}
          </div>
        </div>
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-zinc-100 bg-zinc-50 text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">
          <div className="col-span-4">Business</div>
          <div className="col-span-3">Owner</div>
          <div className="col-span-1 text-right">Users</div>
          <div className="col-span-1 text-right">Leads</div>
          <div className="col-span-1 text-right">Invoices</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-6 text-zinc-500 text-center">Loading…</div>
        ) : filteredBiz.length === 0 ? (
          <div className="p-6 text-zinc-500 text-center" data-testid="superadmin-search-empty">
            {query ? `No businesses match "${query}".` : "No businesses yet."}
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filteredBiz.map((b) => (
              <div key={b.id} className={`grid grid-cols-12 gap-4 px-6 py-4 items-center ${b.disabled ? "bg-rose-50/40" : ""}`} data-testid={`superadmin-row-${b.id}`}>
                <div className="col-span-4">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-zinc-400"/>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {b.business_name}
                        {b.disabled && <span className="text-[10px] px-2 py-0.5 rounded bg-rose-100 text-rose-700 uppercase font-mono-accent">disabled</span>}
                        {b.id === user.business_id && <span className="text-[10px] px-2 py-0.5 rounded bg-orange-100 text-[#FF5A00] uppercase font-mono-accent">yours</span>}
                      </div>
                      <div className="text-xs text-zinc-500">{b.service_type || "—"}</div>
                    </div>
                  </div>
                </div>
                <div className="col-span-3 text-sm">
                  <div>{b.owner_name || "—"}</div>
                  <div className="text-xs text-zinc-500 font-mono-accent">{b.owner_email || "—"}</div>
                </div>
                <div className="col-span-1 text-right text-sm font-mono-accent flex items-center justify-end gap-1"><Users2 className="w-3.5 h-3.5 text-zinc-400"/>{b.users_count}</div>
                <div className="col-span-1 text-right text-sm font-mono-accent flex items-center justify-end gap-1"><UserPlus className="w-3.5 h-3.5 text-zinc-400"/>{b.leads_count}</div>
                <div className="col-span-1 text-right text-sm font-mono-accent flex items-center justify-end gap-1"><Receipt className="w-3.5 h-3.5 text-zinc-400"/>{b.invoices_count}</div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  <button
                    onClick={()=>impersonate(b)}
                    disabled={b.disabled}
                    className="text-xs px-3 py-1.5 rounded border border-zinc-200 hover:border-[#FF5A00] hover:text-[#FF5A00] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                    data-testid={`superadmin-impersonate-${b.id}`}
                  >
                    <LogIn className="w-3 h-3"/> View
                  </button>
                  <button
                    onClick={()=>toggle(b)}
                    className={`text-xs px-3 py-1.5 rounded border flex items-center gap-1 ${b.disabled ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-rose-300 text-rose-700 hover:bg-rose-50"}`}
                    data-testid={`superadmin-toggle-${b.id}`}
                  >
                    {b.disabled ? <><ShieldCheck className="w-3 h-3"/>Enable</> : <><ShieldOff className="w-3 h-3"/>Disable</>}
                  </button>
                  {b.id !== user.business_id && (
                    <button
                      onClick={()=>del(b)}
                      className="p-1.5 rounded border border-zinc-200 hover:border-rose-500 hover:text-rose-600"
                      title="Delete business permanently"
                      data-testid={`superadmin-delete-${b.id}`}
                    >
                      <Trash2 className="w-3 h-3"/>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create business account</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label>Business name</Label>
              <Input required value={form.business_name} onChange={(e)=>setForm({...form, business_name: e.target.value})} data-testid="superadmin-form-biz-name"/>
            </div>
            <div>
              <Label>Service type</Label>
              <Input value={form.service_type} onChange={(e)=>setForm({...form, service_type: e.target.value})} placeholder="e.g. HVAC, Roofing"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Owner name</Label>
                <Input required value={form.owner_full_name} onChange={(e)=>setForm({...form, owner_full_name: e.target.value})} data-testid="superadmin-form-owner-name"/>
              </div>
              <div>
                <Label>Owner email</Label>
                <Input type="email" required value={form.owner_email} onChange={(e)=>setForm({...form, owner_email: e.target.value})} data-testid="superadmin-form-owner-email"/>
              </div>
            </div>
            <div>
              <Label>Temporary password (min 8 chars)</Label>
              <Input type="password" required minLength={8} value={form.owner_password} onChange={(e)=>setForm({...form, owner_password: e.target.value})} data-testid="superadmin-form-owner-pw"/>
              <p className="text-xs text-zinc-500 mt-1">Owner should change this via password reset after first login.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="superadmin-form-submit">
                {busy ? "Creating…" : "Create business"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
